import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle,
  XCircle,
  RefreshCw,
  ExternalLink,
  ShieldCheck,
  Smartphone,
  FlaskConical,
  Search,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { SUPABASE_URL, SUPABASE_ANON_KEY } from '../../config/env.js';
import { MARKETING_SITE_URL } from '../../services/marketingAdminService.js';
import MechanicAppPreviewModal from '../../components/admin/MechanicAppPreviewModal';

const headers = {
  apikey: SUPABASE_ANON_KEY,
  Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
  'Content-Type': 'application/json',
};

const DOC_TYPE_LABEL = {
  selfie: 'Selfie',
  aadhaar: 'Aadhaar front',
  aadhaar_front: 'Aadhaar front',
  aadhaar_back: 'Aadhaar back',
  pan: 'PAN',
  shop_license: 'Shop license',
  other: 'Other',
};

const DOC_REQUIRED = {
  selfie: true,
  aadhaar: true,
  aadhaar_front: true,
  aadhaar_back: true,
  pan: false,
  shop_license: false,
  other: false,
};

const DOC_ORDER = [
  'selfie',
  'aadhaar_front',
  'aadhaar',
  'aadhaar_back',
  'pan',
  'shop_license',
  'other',
];

const REQUIRED_SLOTS = [
  { key: 'selfie', types: ['selfie'], label: 'Selfie' },
  { key: 'front', types: ['aadhaar_front', 'aadhaar'], label: 'Aadhaar' },
  { key: 'back', types: ['aadhaar_back'], label: 'Back' },
];

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'pending', label: 'Pending' },
  { id: 'approved', label: 'Approved' },
  { id: 'rejected', label: 'Rejected' },
  { id: 'none', label: 'No docs' },
];

const statusClass = (status) => {
  if (status === 'pending') return 'bg-amber-50 text-amber-700';
  if (status === 'approved' || status === 'verified') return 'bg-emerald-50 text-emerald-700';
  if (status === 'rejected') return 'bg-red-50 text-red-700';
  return 'bg-slate-100 text-slate-500';
};

const slotDot = (status) => {
  if (status === 'approved') return 'bg-emerald-500';
  if (status === 'pending') return 'bg-amber-400';
  if (status === 'rejected') return 'bg-red-500';
  return 'bg-slate-300';
};

const parseTotal = (contentRange) => {
  const m = String(contentRange || '').match(/\/(\d+)/);
  return m ? Number(m[1]) : 0;
};

const fetchPaged = async (url) => {
  const pageSize = 200;
  let from = 0;
  let all = [];
  let total = 0;
  for (let i = 0; i < 25; i += 1) {
    const res = await fetch(url, {
      headers: {
        ...headers,
        Prefer: 'count=exact',
        'Range-Unit': 'items',
        Range: `${from}-${from + pageSize - 1}`,
      },
    });
    const chunk = await res.json().catch(() => []);
    const list = Array.isArray(chunk) ? chunk : [];
    total = parseTotal(res.headers.get('content-range')) || total;
    all = all.concat(list);
    if (!list.length || all.length >= total || list.length < pageSize) break;
    from += pageSize;
  }
  return { list: all, total: total || all.length };
};

const partnerBucket = (g) => {
  const kyc = String(g.kyc_status || '').toLowerCase();
  if (kyc === 'verified') return 'approved';
  if (kyc === 'rejected' || g.docs.some((d) => d.status === 'rejected')) return 'rejected';
  if (g.pendingCount > 0 || kyc === 'pending') return 'pending';
  return 'none';
};

const rpc = async (name, body) => {
  const res = await fetch(`${SUPABASE_URL}/rest/v1/rpc/${name}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body || {}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = data?.message || data?.error || `RPC ${name} failed`;
    throw new Error(msg);
  }
  return data;
};

const trainingPhase = (job) => {
  if (!job) return null;
  if (job.mechanic_started_datetime) return 'collect';
  if (job.arrival_otp_verified_at || job.mechanic_reached_datetime) return 'started';
  if (job.assigned_mechanic_id) return 'otp';
  if (job.quote_amount != null) return 'quoted';
  return 'waiting';
};

const PHASE_COPY = {
  waiting: 'Waiting for partner rate quote.',
  quoted: 'Partner sent a rate. Accept to assign.',
  otp: 'Assigned. Share OTP — then Arrived → collect → Complete.',
  started: 'OTP verified. Partner can start work, collect, then Complete.',
  collect: 'Work started. Collect amount, then Complete.',
};

const TrainingJobBar = ({ job, busyId, onAccept, onEnd }) => {
  const phase = trainingPhase(job);
  const amount = job.quote_amount ?? job.agreed_amount;
  return (
    <div className="border-t border-violet-100 bg-violet-50 px-4 py-2.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1 rounded-md bg-violet-600 px-2 py-0.5 text-[11px] font-bold text-white">
            <FlaskConical size={12} /> TEST
          </span>
          <span className="text-xs font-semibold text-violet-900">{job.service_type || 'Jump Start'}</span>
          {amount != null ? (
            <span className="rounded-md bg-white px-2 py-0.5 text-[11px] font-bold text-violet-800">
              ₹{Number(amount)}
            </span>
          ) : null}
          {phase === 'otp' && job.arrival_otp ? (
            <span className="rounded-md bg-white px-2 py-0.5 font-mono text-sm font-bold tracking-widest text-slate-900">
              OTP {job.arrival_otp}
            </span>
          ) : null}
          <span className="text-xs text-violet-800">{PHASE_COPY[phase] || PHASE_COPY.waiting}</span>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {phase === 'quoted' ? (
            <button
              type="button"
              disabled={!!busyId}
              onClick={onAccept}
              className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
            >
              <CheckCircle size={14} />
              Accept
            </button>
          ) : null}
          <button
            type="button"
            disabled={!!busyId}
            onClick={onEnd}
            className="rounded-lg border border-violet-200 bg-white px-3 py-1.5 text-xs font-semibold text-violet-800 disabled:opacity-50"
          >
            End test
          </button>
        </div>
      </div>
    </div>
  );
};

const findDoc = (docs, types) => docs.find((d) => types.includes(d.doc_type));

const MechanicKycVerify = () => {
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [previewGroup, setPreviewGroup] = useState(null);
  const [trainingById, setTrainingById] = useState({});
  const [trainingMsg, setTrainingMsg] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [partnerTotal, setPartnerTotal] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const mechSelect =
        'id,code,mechanic_name,shop_name,number,kyc_status,is_public,active,public_slug,city,area,state,district,city_slug,area_slug,name_slug,logo_url,created_at';
      const mechSelectFallback =
        'id,code,mechanic_name,shop_name,number,kyc_status,is_public,active,public_slug,city,area,logo_url,created_at';
      let mechPage = await fetchPaged(
        `${SUPABASE_URL}/rest/v1/mechanics?select=${mechSelect}&order=created_at.desc`,
      );
      if (!mechPage.list.length) {
        mechPage = await fetchPaged(
          `${SUPABASE_URL}/rest/v1/mechanics?select=${mechSelectFallback}&order=created_at.desc`,
        );
      }
      const [docPage, rpcDocs] = await Promise.all([
        fetchPaged(
          `${SUPABASE_URL}/rest/v1/mechanic_kyc_documents?select=id,mechanic_id,doc_type,file_url,status,admin_note,created_at,reviewed_at&order=created_at.desc`,
        ).catch(() => ({ list: [], total: 0 })),
        rpc('admin_list_mechanic_kyc', { p_status: 'all' }).catch(() => []),
      ]);
      const mechList = mechPage.list;
      setPartnerTotal(mechPage.total || mechList.length);

      const fromTable = docPage.list.map((d) => ({
        document_id: d.id,
        mechanic_id: d.mechanic_id,
        doc_type: d.doc_type,
        file_url: d.file_url,
        status: d.status,
        admin_note: d.admin_note,
      }));
      const fromRpc = Array.isArray(rpcDocs) ? rpcDocs : [];
      const docRows = fromTable.length ? fromTable : fromRpc;

      if (!mechList.length) {
        setRows(docRows);
        setPartnerTotal(new Set(docRows.map((d) => d.mechanic_id).filter(Boolean)).size);
        return;
      }

      const byMech = new Map();
      for (const d of docRows) {
        const id = d.mechanic_id;
        if (!id) continue;
        if (!byMech.has(id)) byMech.set(id, []);
        byMech.get(id).push(d);
      }

      const merged = [];
      for (const m of mechList) {
        const existing = byMech.get(m.id) || [];
        if (existing.length) {
          merged.push(
            ...existing.map((d) => ({
              ...d,
              code: d.code || m.code,
              mechanic_name: d.mechanic_name || m.mechanic_name,
              shop_name: d.shop_name || m.shop_name,
              phone: d.phone || m.number,
              kyc_status: m.kyc_status,
              is_public: m.is_public,
              active: m.active,
              public_slug: m.public_slug,
              city: m.city,
              area: m.area,
              state: m.state,
              district: m.district,
              logo_url: m.logo_url,
            })),
          );
        } else {
          merged.push({
            document_id: null,
            mechanic_id: m.id,
            doc_type: null,
            file_url: null,
            status: null,
            admin_note: null,
            code: m.code,
            mechanic_name: m.mechanic_name,
            shop_name: m.shop_name,
            phone: m.number,
            kyc_status: m.kyc_status,
            is_public: m.is_public,
            active: m.active,
            public_slug: m.public_slug,
            city: m.city,
            area: m.area,
            state: m.state,
            district: m.district,
            logo_url: m.logo_url,
            created_at: m.created_at,
          });
        }
      }
      setRows(merged);
    } catch (e) {
      console.error(e);
      setRows([]);
      setPartnerTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const loadTraining = async (mechanicIds) => {
    const ids = (mechanicIds || []).filter(Boolean);
    if (!ids.length) {
      setTrainingById({});
      return;
    }
    try {
      const data = await rpc('admin_list_mechanic_training_jobs', { p_mechanic_ids: ids });
      const jobs = Array.isArray(data?.jobs) ? data.jobs : Array.isArray(data) ? data : [];
      const map = {};
      for (const j of jobs) {
        if (j?.mechanic_id) map[j.mechanic_id] = j;
      }
      setTrainingById(map);
      setTrainingMsg('');
    } catch (e) {
      const msg = String(e?.message || '');
      if (/could not find the function|schema cache|PGRST202/i.test(msg)) {
        setTrainingMsg('Test job SQL pending: run supabase/mechanic-training-job.sql in Supabase.');
      }
    }
  };

  const groups = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      const key = r.mechanic_id || r.document_id;
      if (!key) continue;
      if (!map.has(key)) {
        map.set(key, {
          mechanic_id: r.mechanic_id,
          shop_name: r.shop_name || r.mechanic_name || r.code || 'Partner',
          phone: r.phone,
          code: r.code,
          kyc_status: r.kyc_status,
          is_public: r.is_public,
          public_slug: r.public_slug,
          city: r.city,
          area: r.area,
          state: r.state,
          district: r.district,
          active: r.active,
          docs: [],
        });
      }
      if (r.document_id && r.doc_type) {
        map.get(key).docs.push(r);
      }
    }
    return Array.from(map.values()).map((g) => ({
      ...g,
      docs: [...g.docs].sort(
        (a, b) =>
          (DOC_ORDER.indexOf(a.doc_type) + 1 || 99) - (DOC_ORDER.indexOf(b.doc_type) + 1 || 99),
      ),
      pendingCount: g.docs.filter((d) => d.status === 'pending').length,
    }));
  }, [rows]);

  const counts = useMemo(() => {
    const c = { all: groups.length, pending: 0, approved: 0, rejected: 0, none: 0 };
    for (const g of groups) {
      c[partnerBucket(g)] += 1;
    }
    return c;
  }, [groups]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return groups.filter((g) => {
      if (statusFilter !== 'all' && partnerBucket(g) !== statusFilter) return false;
      if (!q) return true;
      return [g.shop_name, g.phone, g.code, g.public_slug]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [groups, statusFilter, search]);

  useEffect(() => {
    const ids = groups.map((g) => g.mechanic_id).filter(Boolean);
    loadTraining(ids);
    const t = setInterval(() => loadTraining(ids), 8000);
    return () => clearInterval(t);
  }, [groups]);

  const refreshTraining = () => loadTraining(groups.map((g) => g.mechanic_id).filter(Boolean));

  const startTraining = async (mechanicId) => {
    setBusyId(`train-${mechanicId}`);
    try {
      const data = await rpc('admin_start_mechanic_training_job', { p_mechanic_id: mechanicId });
      if (data?.success === false) {
        window.alert(data.error || 'Could not start test job');
      } else if (data?.request_id) {
        fetch(`${SUPABASE_URL}/functions/v1/partner-job-notify`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ request_id: data.request_id }),
        }).catch(() => {});
      }
      await refreshTraining();
    } catch (e) {
      window.alert(e?.message || 'Could not start test job. Run mechanic-training-job.sql first.');
    } finally {
      setBusyId(null);
    }
  };

  const acceptTraining = async (requestId) => {
    setBusyId(`accept-${requestId}`);
    try {
      const data = await rpc('admin_accept_training_quote', { p_request_id: requestId });
      if (data?.success === false) {
        window.alert(data.error || 'Accept failed');
      }
      await refreshTraining();
    } catch (e) {
      window.alert(e?.message || 'Accept failed');
    } finally {
      setBusyId(null);
    }
  };

  const endTraining = async (requestId, mechanicId) => {
    setBusyId(`end-${requestId}`);
    try {
      await rpc('admin_purge_training_job', { p_request_id: requestId });
      setTrainingById((prev) => {
        const next = { ...prev };
        delete next[mechanicId];
        return next;
      });
      await refreshTraining();
    } catch (e) {
      window.alert(e?.message || 'Could not end test');
    } finally {
      setBusyId(null);
    }
  };

  const review = async (documentId, approve) => {
    const note = approve ? '' : window.prompt('Rejection reason (optional)') || 'Rejected by admin';
    setBusyId(documentId);
    try {
      const res = await fetch(`${SUPABASE_URL}/rest/v1/rpc/admin_review_mechanic_kyc_doc`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          p_document_id: documentId,
          p_approve: approve,
          p_admin_note: note || null,
          p_reviewed_by: null,
        }),
      });
      const data = await res.json();
      if (!res.ok || data?.success === false) {
        alert(data?.error || data?.message || 'Review failed');
      } else {
        load();
      }
    } finally {
      setBusyId(null);
    }
  };

  const approveAllPending = async (docs) => {
    const pending = docs.filter((d) => d.status === 'pending');
    if (!pending.length) return;
    if (!window.confirm(`Approve all ${pending.length} pending document(s) for this partner?`)) {
      return;
    }
    for (const d of pending) {
      setBusyId(d.document_id);
      try {
        const res = await fetch(`${SUPABASE_URL}/rest/v1/rpc/admin_review_mechanic_kyc_doc`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            p_document_id: d.document_id,
            p_approve: true,
            p_admin_note: null,
            p_reviewed_by: null,
          }),
        });
        const data = await res.json();
        if (!res.ok || data?.success === false) {
          alert(data?.error || data?.message || 'Approve all failed');
          break;
        }
      } finally {
        setBusyId(null);
      }
    }
    load();
  };

  return (
    <div className="mx-auto w-full max-w-[1280px] space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Partner KYC</h1>
          <p className="mt-1 text-sm text-slate-500">
            selfie + Aadhaar front/back required · tap a row to review
          </p>
          <div className="mt-2 inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Total partners</span>
            <span className="text-lg font-bold tabular-nums text-slate-900">
              {(partnerTotal || groups.length).toLocaleString('en-IN')}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/mechanic-management"
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700"
          >
            Partner list
          </Link>
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-semibold text-white"
          >
            <RefreshCw size={14} />
            Refresh
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <label className="relative min-w-[220px] flex-1 max-w-sm">
          <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, phone, code"
            className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-8 pr-3 text-sm outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
          />
        </label>
        <div className="flex gap-1 rounded-lg border border-slate-200 bg-slate-50 p-0.5">
          {FILTERS.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setStatusFilter(s.id)}
              className={`rounded-md px-2.5 py-1.5 text-xs font-semibold ${
                statusFilter === s.id ? 'bg-indigo-600 text-white' : 'text-slate-600'
              }`}
            >
              {s.label}
              <span className={`ml-1 ${statusFilter === s.id ? 'text-indigo-100' : 'text-slate-400'}`}>
                {counts[s.id] ?? 0}
              </span>
            </button>
          ))}
        </div>
        <span className="text-xs font-semibold text-slate-500">
          Showing {visible.length.toLocaleString('en-IN')} of{' '}
          {(partnerTotal || groups.length).toLocaleString('en-IN')}
        </span>
        {trainingMsg ? <span className="text-xs font-semibold text-amber-700">{trainingMsg}</span> : null}
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        {loading ? (
          <div className="p-10 text-center text-sm text-slate-500">Loading…</div>
        ) : visible.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500">No partners in this filter</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px] text-left text-sm">
              <thead className="border-b border-slate-100 bg-slate-50 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="w-8 px-3 py-3" />
                  <th className="px-3 py-3">Partner</th>
                  <th className="px-3 py-3">Phone</th>
                  <th className="px-3 py-3">KYC</th>
                  <th className="px-3 py-3">Docs</th>
                  <th className="px-3 py-3">Public</th>
                  <th className="px-3 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {visible.map((g) => {
                  const open = expandedId === g.mechanic_id;
                  const kyc = String(g.kyc_status || 'none').toLowerCase();
                  const verified = kyc === 'verified';
                  return (
                    <React.Fragment key={g.mechanic_id || g.shop_name}>
                      <tr
                        className={`cursor-pointer ${open ? 'bg-indigo-50/40' : 'hover:bg-slate-50'}`}
                        onClick={() => setExpandedId(open ? null : g.mechanic_id)}
                      >
                        <td className="px-3 py-3 text-slate-400">
                          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        </td>
                        <td className="px-3 py-3">
                          <div className="font-semibold text-slate-900">{g.shop_name}</div>
                          <div className="text-[11px] text-slate-400">
                            {g.code || '—'}
                            {g.active === false ? ' · Offline' : ''}
                            {trainingById[g.mechanic_id] ? ' · Test live' : ''}
                          </div>
                        </td>
                        <td className="px-3 py-3 tabular-nums text-slate-600">{g.phone || '—'}</td>
                        <td className="px-3 py-3">
                          <span
                            className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-bold uppercase ${statusClass(
                              verified ? 'verified' : kyc,
                            )}`}
                          >
                            {verified ? <ShieldCheck size={12} /> : null}
                            {kyc === 'verified' ? 'Verified' : kyc === 'rejected' ? 'Rejected' : kyc === 'pending' ? 'Pending' : 'None'}
                          </span>
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-3">
                            {REQUIRED_SLOTS.map((slot) => {
                              const doc = findDoc(g.docs, slot.types);
                              const st = doc?.status || 'missing';
                              return (
                                <span key={slot.key} className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-600">
                                  <span className={`h-2 w-2 rounded-full ${slotDot(st)}`} />
                                  {slot.label}
                                </span>
                              );
                            })}
                            {g.pendingCount ? (
                              <span className="rounded-md bg-amber-50 px-1.5 py-0.5 text-[10px] font-bold text-amber-700">
                                {g.pendingCount} pending
                              </span>
                            ) : null}
                          </div>
                        </td>
                        <td className="px-3 py-3">
                          <span className={`text-xs font-semibold ${g.is_public ? 'text-emerald-700' : 'text-slate-400'}`}>
                            {g.is_public ? 'On' : 'Off'}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="inline-flex items-center gap-1.5">
                            <button
                              type="button"
                              onClick={() => setPreviewGroup(g)}
                              className="inline-flex items-center gap-1 rounded-lg px-2 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-50"
                              title="Partner app preview"
                            >
                              <Smartphone size={14} />
                              App
                            </button>
                            {verified && g.mechanic_id ? (
                              <button
                                type="button"
                                disabled={!!busyId || !!trainingById[g.mechanic_id]}
                                onClick={() => startTraining(g.mechanic_id)}
                                className="inline-flex items-center gap-1 rounded-lg bg-violet-600 px-2 py-1.5 text-xs font-bold text-white disabled:opacity-50"
                              >
                                <FlaskConical size={12} />
                                {busyId === `train-${g.mechanic_id}` ? '…' : 'Test'}
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                      {open ? (
                        <tr className="bg-slate-50/80">
                          <td colSpan={7} className="px-0 py-0">
                            {trainingById[g.mechanic_id] ? (
                              <TrainingJobBar
                                job={trainingById[g.mechanic_id]}
                                busyId={busyId}
                                onAccept={() => acceptTraining(trainingById[g.mechanic_id].request_id)}
                                onEnd={() =>
                                  endTraining(trainingById[g.mechanic_id].request_id, g.mechanic_id)
                                }
                              />
                            ) : null}
                            <div className="flex items-center justify-between px-4 py-2">
                              <p className="text-xs text-slate-500">
                                {g.docs.length
                                  ? `${g.docs.length} document${g.docs.length === 1 ? '' : 's'}`
                                  : 'No KYC documents uploaded yet'}
                                {g.public_slug ? (
                                  <>
                                    {' · '}
                                    <a
                                      href={`${String(MARKETING_SITE_URL).replace(/\/$/, '')}/m/${g.public_slug}`}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="font-semibold text-indigo-600 hover:underline"
                                    >
                                      /m/{g.public_slug}
                                    </a>
                                  </>
                                ) : (
                                  ''
                                )}
                                {[g.area, g.district || g.city, g.state].filter(Boolean).length ? (
                                  <span className="text-slate-400">
                                    {' · '}
                                    {[g.area, g.district || g.city, g.state].filter(Boolean).join(', ')}
                                  </span>
                                ) : null}
                              </p>
                              {g.pendingCount > 1 ? (
                                <button
                                  type="button"
                                  disabled={!!busyId}
                                  onClick={() => approveAllPending(g.docs)}
                                  className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
                                >
                                  <CheckCircle size={14} />
                                  Approve all pending
                                </button>
                              ) : null}
                            </div>
                            {g.docs.length ? (
                              <div className="grid gap-2 px-4 pb-4 sm:grid-cols-2 lg:grid-cols-3">
                                {g.docs.map((r) => (
                                  <div
                                    key={r.document_id}
                                    className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-2.5"
                                  >
                                    <a
                                      href={r.file_url}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="h-14 w-14 shrink-0 overflow-hidden rounded-md border border-slate-200 bg-slate-50"
                                    >
                                      <img
                                        src={r.file_url}
                                        alt={r.doc_type}
                                        className="h-full w-full object-cover"
                                      />
                                    </a>
                                    <div className="min-w-0 flex-1">
                                      <div className="flex flex-wrap items-center gap-1.5">
                                        <span className="text-xs font-semibold text-slate-900">
                                          {DOC_TYPE_LABEL[r.doc_type] || r.doc_type}
                                        </span>
                                        {DOC_REQUIRED[r.doc_type] ? (
                                          <span className="text-[10px] font-bold text-slate-400">REQ</span>
                                        ) : null}
                                        <span
                                          className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${statusClass(
                                            r.status,
                                          )}`}
                                        >
                                          {r.status}
                                        </span>
                                      </div>
                                      {r.admin_note ? (
                                        <p className="mt-0.5 truncate text-[11px] text-red-600">{r.admin_note}</p>
                                      ) : null}
                                      <div className="mt-1.5 flex items-center gap-1.5">
                                        <a
                                          href={r.file_url}
                                          target="_blank"
                                          rel="noreferrer"
                                          className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-slate-500 hover:text-slate-800"
                                        >
                                          <ExternalLink size={12} />
                                          Open
                                        </a>
                                        {r.status === 'pending' ? (
                                          <>
                                            <button
                                              type="button"
                                              disabled={busyId === r.document_id}
                                              onClick={() => review(r.document_id, true)}
                                              className="rounded bg-emerald-600 px-2 py-0.5 text-[11px] font-semibold text-white disabled:opacity-50"
                                            >
                                              Approve
                                            </button>
                                            <button
                                              type="button"
                                              disabled={busyId === r.document_id}
                                              onClick={() => review(r.document_id, false)}
                                              className="rounded bg-red-600 px-2 py-0.5 text-[11px] font-semibold text-white disabled:opacity-50"
                                            >
                                              Reject
                                            </button>
                                          </>
                                        ) : null}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="px-4 pb-4 text-xs text-slate-400">
                                Partner has not uploaded selfie / Aadhaar yet.
                              </p>
                            )}
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {previewGroup?.mechanic_id ? (
        <MechanicAppPreviewModal
          mechanicId={previewGroup.mechanic_id}
          initialGroup={previewGroup}
          onClose={() => setPreviewGroup(null)}
          onChanged={load}
        />
      ) : null}
    </div>
  );
};

export default MechanicKycVerify;
